import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, of, throwError } from 'rxjs';
import { ApiUrl } from '../constants/api-url';
import { FormApprovalStatus, FormRequestStatus } from '../modules/supplier-view/supplier-view.constants';

@Injectable({
  providedIn: 'root'
})
export class SupplierService {

  private httpClient = inject(HttpClient);

  constructor() { }

  getSupplierSettings(key: string) {
    return this.httpClient.get(`${ApiUrl?.SUPPLIER_GET_SETTINGS}${key}`).pipe(
      // SM-4011: a 404 here means the setting doesn't exist for this tenant (e.g. a
      // CCRA-only setting on a SEM tenant) - treat as absent rather than an error.
      // Previously returning `error` directly was not a valid Observable and caused
      // RxJS to throw an unhandled "invalid stream" TypeError on every page load.
      catchError(() => of(null))
    );
  }

  getOrgAndLinks() {
    return this.httpClient.get(`${ApiUrl?.SUPPLIER_GET_ORG}`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  getOranizationUserList(orgUniqueId: any) {
    return this.httpClient.get(`${ApiUrl?.SUPPLIER_GET_ORG_USER_LIST}${orgUniqueId}`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  getUserRoleInformation(userUniqueId: any) {
    return this.httpClient.get(`${ApiUrl?.GET_USER_ROLE_INFORMATION}${userUniqueId}`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  updateUserRole(userUniqueId: any, status: any, roles: string[]) {
    return this.httpClient.post(`${ApiUrl.UPDATE_USER_ROLE}UserUniqueID=${userUniqueId}&Status=${status}`, roles, {}).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  getOrganizationFormRequest(formRequestStatus: FormRequestStatus, formApprovalStatus: FormApprovalStatus) {
    return this.httpClient.get(`${ApiUrl?.GET_SUPPLIER_ORGANIZATION_FORM_REQUEST}?formRequestStatus=${formRequestStatus}&formApprovalStatus=${formApprovalStatus}`).pipe(
      // SM-4011: rethrow as an Observable so the subscriber's error handler fires
      // instead of RxJS throwing an unhandled "invalid stream" TypeError. That
      // unhandled throw was aborting onSuccessfulSearch before it ran, leaving the
      // Completed Forms tab stuck on "No Records Found" even when forms existed.
      catchError((error) => throwError(() => error))
    );
  }

  getOrganizationFormRequestForSearch(formRequestStatus: FormRequestStatus, formApprovalStatus: FormApprovalStatus, RequestNumber: string, FormName: string, FormStatus: string, BuyerApprovalStatus: string, StartDate: any) {
    const params: any = {
      formRequestStatus,
      formApprovalStatus
    };

    // Only add parameters that have values
    if (RequestNumber) params.requestNumber = RequestNumber;
    if (FormName) params.formName = FormName;
    if (FormStatus) params.status = FormStatus;
    if (BuyerApprovalStatus) params.buyerApprovalStatus = BuyerApprovalStatus;
    if (StartDate) params.startDate = StartDate;

    const queryString = new URLSearchParams(params).toString();
    
    return this.httpClient.get(`${ApiUrl?.GET_SUPPLIER_ORGANIZATION_FORM_REQUEST}?${queryString}`, {headers:{'api-version': '2'}}).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getSupplierApproverDetails(AssignedToId: any, formReqRefId: any) {
    return this.httpClient.get(`${ApiUrl?.ASSIGN_SUPPLIER_APPROVER}?AssignedToId=${AssignedToId}&formReqRefId=${formReqRefId}`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  getReassignDropdownList(FormRequestRefId: any) {
    return this.httpClient.get(`${ApiUrl?.SUPPLIER_REASSIGN}?AssignedToId=null&FormRequestRefId=${FormRequestRefId}&PartnerType=Supplier`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  downloadRevision(requestString: string) {

    const headers = new HttpHeaders({'Content-Type': 'application/json', 'Accept': 'application/pdf'});
    return this.httpClient.get(`${ApiUrl?.DOWNLOAD_REVISION}${requestString}`, { headers, responseType: 'blob'  }).pipe(
      catchError((error) => {
        return error;
      })
    );
  }
  downloadOfflineForm(requestString: string) {
    return this.httpClient.get(`${ApiUrl?.DOWNLOAD_OFFLINE_FORM}${requestString}`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  fetchFormApproverSummary(PartnerType: any, formReqRefId: any) {
    return this.httpClient.get(`${ApiUrl?.SHARED_APPROVER_SUMMARY}?PartnerType=${PartnerType}&formReqRefId=${formReqRefId}`, {}).pipe(
      catchError((error) => {
        return error;
      })
    );
  }



  supplierFormReassign(AssignedToId: any, formReqRefId: any) {
    return this.httpClient.post(`${ApiUrl?.SUPPLIER_REASSIGN}?AssignedToId=${AssignedToId}&formReqRefId=${formReqRefId}`, {}).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  supplierFormAssignApprovers(AssignedToId: any, formReqRefId: any) {
    return this.httpClient.get(`${ApiUrl?.ASSIGN_SUPPLIER_APPROVERS}?AssignedToId=${AssignedToId}&FormRequestRefId=${formReqRefId}&PartnerType=Supplier`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }
  supplierFormRenewRequest(formReqRefId: any) {
    return this.httpClient.get(`${ApiUrl?.SUPPLIER_FORM_RENEW_REQUEST}?formReqRefId=${formReqRefId}`).pipe(
      catchError((error) => {
        // SM-3554: rethrow as an Observable so the subscriber's error handler fires.
        // Previously returning `error` directly was not a valid Observable and caused
        // RxJS to throw an unhandled exception, silencing the error entirely.
        return throwError(() => error);
      })
    );
  }

  deleteBanners(data: any) {
    return this.httpClient.post(`${ApiUrl?.DELETE_BANNER}`, data).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  formAssignedApproverUsers(data: any) {
    return this.httpClient.post(`${ApiUrl?.FORM_ASSIGNED_APPROVER_USER}?${data}`, {}).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  supplierFormRequestDetails(data: any) {
    return this.httpClient.get(`${ApiUrl?.GET_SUPPLIER_FORM_REQUEST_DETAILS}${data}`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  supplierFormInputTrackRead(formRequestRefId: string) {
    return this.httpClient.get(`${ApiUrl?.SUPPLIER_FORM_INPUT_TRACK_READ}${formRequestRefId}`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }
  supplierDelegationRead(orgFormRefID: string, data: any) {
    return this.httpClient.post(`${ApiUrl?.SUPPLIER_FORM_DELEGATION_READ}${orgFormRefID}`, data).pipe(
      catchError((error) => {
        return error;
      })
    );
  }
  fetchSupplierCommunications(rowCount: string) {
    return this.httpClient.get(`${ApiUrl?.SUPPLIER_COMMUNICATIONS}${rowCount}`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }
  
  addAnswerToQuestion(payload: any) {
    return this.httpClient.post(`${ApiUrl?.ADD_ANSWER_TO_QUESTION}`, payload, { responseType: 'text' }).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  submitQuestion(payload: any) {
    return this.httpClient.post(`${ApiUrl?.SUBMIT_QUESTION}`, payload, { responseType: 'text' }).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
  supplierCreateProcessTaskForUpgrade(data: any, isByPass: boolean) {
    return this.httpClient.post(`${ApiUrl?.SUPPLIER_CREATE_PROCESS_TASK_FOR_UPGRADE}?isByPass=${isByPass}`, data).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  supplierAutoUpgrade(formUpgradeRequest: any) {
    return this.httpClient.post(`${ApiUrl?.SUPPLIER_AUTO_UPGRADE}`, formUpgradeRequest).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  supplierCancelProcessTask(formUpgradeRequest: any) {
    return this.httpClient.post(`${ApiUrl?.SUPPLIER_CANCEL_PROCESS_TASK}`, formUpgradeRequest).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
